import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/base/PageLayout';

const WisdomEtiquette: React.FC = () => {
  useEffect(() => {
    document.title = 'フォーマルマナー - 執事直伝のテーブルマナー・ドレスコード・社交術｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが教える正統派フォーマルマナー。西洋料理・日本料理のテーブルマナー、ドレスコードの基本、社交の場でのエチケットなど、格式高い場面で自信を持って振る舞うための知識を体系的に解説。フルコースディナー、パーティー、ビジネスシーンなど、様々なフォーマルな場で恥をかかない本格的なマナーが身につきます。');
    }
  }, []);

  const articles = [
    {
      title: 'テーブルマナーの美学',
      excerpt: '正式な食事の場での美しい立ち振る舞いと作法の基本を詳しく解説します。',
      path: '/wisdom/articles/table-manners',
      readTime: '10分',
      category: 'テーブルマナー',
      image: '/images/generated/seq-etiquette-table-manners-butler-1-7a8e68.jpg'
    },
    {
      title: 'ドレスコードの基本',
      excerpt: 'TPOに応じた適切な服装選びと、品格ある装いのポイントをご紹介。',
      path: '/wisdom/articles/dress-code',
      readTime: '8分',
      category: 'ドレスコード',
      image: '/images/generated/seq-etiquette-dress-code-butler-2-029308.jpg'
    },
    {
      title: '社交のエチケット',
      excerpt: 'ビジネスシーンや社交の場で必要な基本的なマナーと心構えを学びます。',
      path: '/wisdom/articles/social-etiquette',
      readTime: '9分',
      category: '社交マナー',
      image: '/images/generated/seq-etiquette-social-butler-3-0e2fb9.jpg'
    }
  ];

  return (
    <PageLayout hero={<>
<section 
          className="relative py-20 md:py-32 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/images/generated/seq-etiquette-hero-table-85c220.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-2xl md:text-6xl font-serif mb-4 md:mb-6">
                フォーマルマナー
              </h1>
              <p className="text-sm md:text-2xl max-w-6xl mx-auto leading-relaxed px-5 sm:px-6 lg:px-8">
                正式な場での立ち振る舞いは、<br />
                あなたの品格と教養を表現する大切な要素です
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-gradient-to-b from-[#f8f8f8] to-white">
        {/* Hero Section */}
        

        {/* Introduction */}
        <section className="py-12 md:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-6 md:mb-8">
                エチケットの重要性
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="text-sm md:text-xl mb-4 md:mb-6">
                  フォーマルマナーは、相手への敬意を表現する言語です。正しいエチケットを身につけることで、どのような場面でも自信を持って振る舞うことができ、周囲からの信頼と尊敬を得ることができます。
                </p>
                <p className="text-sm md:text-lg mb-4 md:mb-6">
                  特に国際的なビジネスシーンや重要な社交の場では、適切なマナーが成功の鍵となることも少なくありません。これらの技術は一朝一夕で身につくものではありませんが、正しい知識と継続的な実践により、必ず習得できるものです。
                </p>
                <p className="text-sm md:text-lg">
                  ここでは、執事文化や国際的なマナープロトコルの研究を通じて学んだ、実際の場面で役立つ実践的なマナーを、具体的な事例とともにお伝えしていきます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Etiquette Categories */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                マナーの基本分野
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-restaurant-line',
                  title: 'テーブルマナー',
                  description: '食事の場での正しい作法と美しい立ち振る舞い'
                },
                {
                  icon: 'ri-shirt-line',
                  title: 'ドレスコード',
                  description: 'TPOに応じた適切な服装選びと着こなし'
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '社交マナー',
                  description: 'ビジネスや社交の場での基本的なエチケット'
                },
                {
                  icon: 'ri-global-line',
                  title: '国際マナー',
                  description: '異文化間でのコミュニケーションと配慮'
                }
              ].map((category, index) => (
                <div key={index} className="bg-white p-6 md:p-8 rounded-lg shadow-md text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <i className={`${category.icon} text-2xl md:text-4xl text-[#917c50]`}></i>
                  </div>
                  <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 md:mb-4">
                    {category.title}
                  </h3>
                  <p className="text-xs md:text-base text-gray-600 leading-relaxed">
                    {category.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Points */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                マナーの心得
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-6 md:space-y-8">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-[#917c50]/10 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-[#917c50] text-sm md:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-serif text-gray-900 mb-2">相手への配慮</h3>
                    <p className="text-xs md:text-base text-gray-600">マナーの根本は相手を思いやる心です。自分の行動が他者にどのような影響を与えるかを常に考えましょう。</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-[#917c50]/10 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-[#917c50] text-sm md:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-serif text-gray-900 mb-2">場の空気を読む</h3>
                    <p className="text-xs md:text-base text-gray-600">その場の雰囲気や文脈を理解し、適切な行動を選択することが重要です。</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-[#917c50]/10 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-[#917c50] text-sm md:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-serif text-gray-900 mb-2">自然な振る舞い</h3>
                    <p className="text-xs md:text-base text-gray-600">形式にとらわれすぎず、自然で心のこもった行動を心がけることが大切です。</p>
                  </div>
                </div>
              </div>
              <div className="space-y-6 md:space-y-8">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-[#917c50]/10 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-[#917c50] text-sm md:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-serif text-gray-900 mb-2">継続的な学習</h3>
                    <p className="text-xs md:text-base text-gray-600">マナーは時代とともに変化します。常に学び続ける姿勢を持ちましょう。</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-[#917c50]/10 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-[#917c50] text-sm md:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-serif text-gray-900 mb-2">実践の重要性</h3>
                    <p className="text-xs md:text-base text-gray-600">知識だけでなく、実際の場面で練習を重ねることで身につきます。</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-[#917c50]/10 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-[#917c50] text-sm md:text-base"></i>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-serif text-gray-900 mb-2">謙虚な姿勢</h3>
                    <p className="text-xs md:text-base text-gray-600">完璧を求めすぎず、間違いから学ぶ謙虚な姿勢を大切にしましょう。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                関連記事
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                フォーマルマナーを実践的に学ぶための詳細な記事
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article, index) => (
                <Link
                  key={index}
                  to={article.path}
                  className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="relative h-48 bg-gradient-to-br from-amber-100 to-amber-200">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-amber-700">
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-black/20 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 group-hover:text-[#917c50] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center text-[#917c50] group-hover:text-[#7a6940]">
                      <span className="text-xs md:text-sm font-medium">記事を読む</span>
                      <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-r from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl md:text-4xl font-serif text-white mb-4 md:mb-6">
              マナー研修をご希望の方へ
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-8 leading-relaxed px-5 sm:px-6 lg:px-8">
              企業研修や個人レッスンを通じて、実践的なフォーマルマナーを身につけていただけます
            </p>
            <Link
              to="/services"
              className="inline-block bg-[#917c50] text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-[#7a6940] transition-colors whitespace-nowrap text-sm md:text-base"
            >
              研修プログラムを見る
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default WisdomEtiquette;
